import type { CSSProperties } from 'react';

// CitationChip — the inline ore-bordered chip used to mark a citation.
// The mock uses a transparent background with an ore border and ore label;
// hovering tints the background with `accent-soft`.
//
// `confidence` modulates the visual emphasis:
//   'high'     → solid ore border (default)
//   'medium'   → dashed ore border
//   'inferred' → dotted ore border with reduced opacity
// `onClick` is optional — when provided the chip becomes a button-like target.
export type CitationConfidence = 'high' | 'medium' | 'inferred';

type CitationChipProps = {
  label: string;
  confidence?: CitationConfidence;
  onClick?: () => void;
  className?: string;
};

export function CitationChip({
  label,
  confidence = 'high',
  onClick,
  className,
}: CitationChipProps) {
  const interactive = typeof onClick === 'function';
  const baseClass = ['ai-cite tabular', className].filter(Boolean).join(' ');
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    padding: '0 7px',
    borderRadius: '999px',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
    fontSize: '10px',
    letterSpacing: '0.02em',
    margin: '4px 4px 0 0',
    background: 'transparent',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background 120ms ease',
    fontFamily: 'inherit',
  };
  if (confidence === 'medium') style.borderStyle = 'dashed';
  if (confidence === 'inferred') {
    style.borderStyle = 'dotted';
    style.opacity = 0.75;
  }

  if (interactive) {
    return (
      <button type="button" className={baseClass} style={style} onClick={onClick}>
        {label}
      </button>
    );
  }
  return (
    <span className={baseClass} style={style}>
      {label}
    </span>
  );
}
